/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { FaComment, FaEdit } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { ToolbarClassic } from '../../../_metronic/layout/components/toolbar/toolbars'
import { getLayoutFromLocalStorage, ILayout, LayoutSetup } from '../../../_metronic/layout/core'
import {
  Claim,
  useClaimDetailsFetcher,
  useClaims,
  useInvalidateClaims,
  useMyTurnTotal
} from '../../../hooks/useClaims'
import { useDebounce } from '../../../hooks/useDebounce'
import { ensureBenefitDetail } from '../../../utils/ensureBenefitDetail'
import { useAuth } from '../../modules/auth'
import ChatModal from '../layout-inpatient/tab-components/ChatModal'

interface BuilderPageProps {
  searchType: string
  searchQuery: string
  onSearchTypeChange: (value: string) => void
  onSearchQueryChange: (value: string) => void
  currentPage: number
  onPageChange: (page: number) => void
}

const BuilderPage: React.FC<BuilderPageProps> = ({
  searchType,
  searchQuery,
  onSearchTypeChange,
  onSearchQueryChange,
  currentPage,
  onPageChange,
}) => {
  const navigate = useNavigate()
  const [setTab] = useState('Sidebar')
  const [config, setConfig] = useState<ILayout>(getLayoutFromLocalStorage())
  const [configLoading, setConfigLoading] = useState<boolean>(false)
  const [resetLoading, setResetLoading] = useState<boolean>(false)
  const { currentUser, auth, logout } = useAuth()
  const [pageSize, setPageSize] = useState(10)
  const [isChatModalOpen, setIsChatModalOpen] = useState<boolean>(false)
  const [claimDetails, setClaimDetails] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'all' | 'myturn'>('all')
  const [loadingClaimNo, setLoadingClaimNo] = useState<string | null>(null)

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  // Show loading indicator when search is being debounced
  const isSearchDebouncing = searchQuery !== debouncedSearchQuery

  // React Query hooks
  const claimsParams = {
    searchType,
    searchQuery: debouncedSearchQuery,
    currentPage,
    pageSize,
    activeTab,
  }

  console.log('BuilderPage - claimsParams:', claimsParams)
  console.log('BuilderPage - currentUser:', currentUser)
  console.log('BuilderPage - auth:', auth)

  const {
    data: claimsData,
    isLoading: loading,
    error: claimsError,
    refetch: refetchClaims,
    isFetching,
    isError,
  } = useClaims(claimsParams)

  console.log('BuilderPage - claimsData:', claimsData)
  console.log('BuilderPage - loading:', loading)
  console.log('BuilderPage - isFetching:', isFetching)
  console.log('BuilderPage - claimsError:', claimsError)
  console.log('BuilderPage - isError:', isError)

  const { data: myTurnTotalRecords = 0, isLoading: myTurnLoading } = useMyTurnTotal()

  const { invalidateAll } = useInvalidateClaims()

  // React Query fetcher for claim details
  const { fetchClaimDetailsOnDemand } = useClaimDetailsFetcher()

  // Extract data from React Query response
  const claims = claimsData?.data || []
  const page = claimsData?.page || {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  }
  const totalRecords = claimsData?.totalRecords || 0
  const error = claimsError ? 'Failed to fetch claims' : null

  // Handle 401 errors from React Query
  useEffect(() => {
    if (claimsError && (claimsError as any)?.response?.status === 401) {
      logout()
      navigate('/login')
    }
  }, [claimsError, logout, navigate])

  // Reset to first page when search parameters change
  useEffect(() => {
    if (currentPage !== 1) {
      onPageChange(1)
    }
  }, [debouncedSearchQuery, searchType, activeTab, onPageChange])

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const updateConfig = () => {
    setConfigLoading(true)
    try {
      LayoutSetup.setConfig(config)
      window.location.reload()
    } catch (error) {
      setConfig(getLayoutFromLocalStorage())
      setConfigLoading(false)
    }
  }

  const reset = () => {
    setResetLoading(true)
    setTimeout(() => {
      setConfig(getLayoutFromLocalStorage())
      setResetLoading(false)
    }, 1000)
  }

  const handleChatAction = async (claim: Claim) => {
    setLoadingClaimNo(claim.claimNo)
    try {
      const details = await fetchClaimDetailsOnDemand(claim.claimNo)
      console.log('Claim details:', details)
      if (details) {
        setClaimDetails(details)
        setIsChatModalOpen(true)
      }
    } catch (error) {
      console.error('Error fetching claim details for chat:', error)
    } finally {
      setLoadingClaimNo(null)
    }
  }

  const handleClaimAction = async (claim: Claim) => {
    setLoadingClaimNo(claim.claimNo)
    try {
      const claimDetails = await fetchClaimDetailsOnDemand(claim.claimNo)
      console.log('Claim details:', claimDetails)
      if (claimDetails) {
        if (!ensureBenefitDetail(claimDetails)) return

        if (claim.treatmentType === 'Rawat jalan') {
          navigate(`/outpatient`, { state: { claimDetails } })
        } else {
          navigate(`/inpatient`, { state: { claimDetails } })
        }
      }
    } catch (error) {
      console.error('Error fetching claim details for navigation:', error)
    } finally {
      setLoadingClaimNo(null)
    }
  }

  const onProviderStatuses = [
    'Pre-Registered',
    'Pending on IGL',
    'IGL RELEASED',
    'Daily Monitoring',
    'Supercreeding request',
    'Pending on FGL',
  ]

  const onHostStatuses = [
    'New Case',
    'Admission Review',
    'Initial Document Complete',
    'Request FGL',
    'FGL Review',
    'Final Document Complete',
  ]

  const getStatusStyles = (status: string) => {
    // Check if the status is in redSectionStatuses
    const isProviderStatus = onProviderStatuses.includes(status)
    const isHostStatus = onHostStatuses.includes(status)

    if (isProviderStatus) {
      return {
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6', // Blue
      }
    } else if (isHostStatus) {
      return {
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981', // Green
      }
    } else {
      return {
        backgroundColor: 'rgba(156, 163, 175, 0.1)',
        color: '#6B7280', // Gray
      }
    }
  }

  // Update filteredClaims to use API data directly
  const filteredClaims = claims

  const renderPagination = () => {
    // Handle case when totalPages object is not yet loaded
    if (!page || !page.totalPages) {
      return null
    }

    const totalPagesCount = page.totalPages
    const currentPageNum = page.currentPage || currentPage
    const totalPagesArray = Array.from({ length: totalPagesCount }, (_, index) => index + 1)
    const pageLimit = 5
    let startPage = Math.max(1, currentPageNum - Math.floor(pageLimit / 2))
    let endPage = Math.min(totalPagesCount, startPage + pageLimit - 1)

    if (endPage - startPage < pageLimit - 1) {
      startPage = Math.max(1, endPage - pageLimit + 1)
    }

    const paginationButtonStyle = {
      minWidth: '36px',
      height: '36px',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.95rem',
      fontWeight: '500',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
      border: '1px solid #e9ecef',
      backgroundColor: '#fff',
      color: '#2D3748',
    }

    const activePaginationStyle = {
      ...paginationButtonStyle,
      backgroundColor: '#EBF7F9',
      borderColor: '#059BB6',
      color: '#059BB6',
    }

    return (
      <div className='d-flex gap-2 align-items-center'>
        <button
          className='btn btn-icon'
          onClick={() => onPageChange(1)}
          disabled={currentPageNum === 1}
          style={{
            ...paginationButtonStyle,
            opacity: currentPageNum === 1 ? 0.5 : 1,
            cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer',
          }}
        >
          «
        </button>

        <button
          className='btn btn-icon'
          onClick={() => onPageChange(currentPageNum - 1)}
          disabled={currentPageNum === 1}
          style={{
            ...paginationButtonStyle,
            opacity: currentPageNum === 1 ? 0.5 : 1,
            cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer',
          }}
        >
          ‹
        </button>

        {startPage > 1 && (
          <>
            <button
              className='btn btn-icon'
              onClick={() => onPageChange(1)}
              style={paginationButtonStyle}
            >
              1
            </button>
            {startPage > 2 && <span style={{ color: '#6B7280', padding: '0 4px' }}>...</span>}
          </>
        )}

        {totalPagesArray.slice(startPage - 1, endPage).map((page) => (
          <button
            key={page}
            className='btn btn-icon'
            onClick={() => onPageChange(page)}
            style={currentPageNum === page ? activePaginationStyle : paginationButtonStyle}
          >
            {page}
          </button>
        ))}

        {endPage < totalPagesCount && (
          <>
            {endPage < totalPagesCount - 1 && (
              <span style={{ color: '#6B7280', padding: '0 4px' }}>...</span>
            )}
            <button
              className='btn btn-icon'
              onClick={() => onPageChange(totalPagesCount)}
              style={paginationButtonStyle}
            >
              {totalPagesCount}
            </button>
          </>
        )}

        <button
          className='btn btn-icon'
          onClick={() => onPageChange(currentPageNum + 1)}
          disabled={currentPageNum === totalPagesCount}
          style={{
            ...paginationButtonStyle,
            opacity: currentPageNum === totalPagesCount ? 0.5 : 1,
            cursor: currentPageNum === totalPagesCount ? 'not-allowed' : 'pointer',
          }}
        >
          ›
        </button>

        <button
          className='btn btn-icon'
          onClick={() => onPageChange(totalPagesCount)}
          disabled={currentPageNum === totalPagesCount}
          style={{
            ...paginationButtonStyle,
            opacity: currentPageNum === totalPagesCount ? 0.5 : 1,
            cursor: currentPageNum === totalPagesCount ? 'not-allowed' : 'pointer',
          }}
        >
          »
        </button>
      </div>
    )
  }

  return (
    <>
      <div
        className='card card-custom'
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          border: '1px solid #e9ecef',
        }}
      >
        <div
          className='card-header'
          style={{
            padding: '0.5rem',
            borderBottom: '1px solid #e9ecef',
            backgroundColor: '#fff',
          }}
        >
          <div className='d-flex flex-column gap-1'>
            <div className='d-flex align-items-center gap-2'>
              <div
                className='d-flex'
                style={{
                  backgroundColor: '#f8f9fa',
                  padding: '2px',
                  borderRadius: '10px',
                  border: '1px solid #e9ecef',
                }}
              >
                <button
                  onClick={() => {
                    setActiveTab('all')
                    invalidateAll()
                  }}
                  className='btn'
                  style={{
                    padding: '8px 24px',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    backgroundColor: activeTab === 'all' ? '#fff' : 'transparent',
                    color: activeTab === 'all' ? '#059BB6' : '#6B7280',
                    boxShadow: activeTab === 'all' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                    border: 'none',
                  }}
                >
                  All Claims
                </button>
                <button
                  onClick={() => {
                    setActiveTab('myturn')
                    invalidateAll()
                  }}
                  className='btn'
                  style={{
                    padding: '8px 24px',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    backgroundColor: activeTab === 'myturn' ? '#fff' : 'transparent',
                    color: activeTab === 'myturn' ? '#059BB6' : '#6B7280',
                    boxShadow: activeTab === 'myturn' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    position: 'relative',
                  }}
                >
                  My Turn
                  {myTurnLoading ? (
                    <span
                      style={{
                        backgroundColor: '#DC2626',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        minWidth: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ...
                    </span>
                  ) : myTurnTotalRecords > 0 ? (
                    <span
                      style={{
                        backgroundColor: '#DC2626',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        minWidth: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {myTurnTotalRecords}
                    </span>
                  ) : null}
                </button>
              </div>
            </div>

            <div className='card-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2'>
              <div className='d-flex gap-2 align-items-center'>
                <div
                  className='input-group'
                  style={{
                    width: '100%',
                    maxWidth: '800px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  }}
                >
                  <select
                    className='form-select'
                    value={searchType}
                    onChange={(e) => onSearchTypeChange(e.target.value)}
                    style={{
                      width: '40%',
                      fontSize: '0.95rem',
                      background: '#f8f9fa',
                      color: '#2D3748',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px 0 0 8px',
                      padding: '0.65rem 1rem',
                      appearance: 'none',
                      backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%232D3748'><path d='M7 10l5 5 5-5H7z'/></svg>\")",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <option value='Nama Member'>Nama Member</option>
                    <option value='Claim Number'>Claim Number</option>
                    <option value='Card Number'>Card Number</option>
                    <option value='Treatment Type'>Tipe Perawatan</option>
                    <option value='Customer Group'>Customer Group</option>
                    <option value='Created Date'>Created Date</option>
                    <option value='Status'>Status</option>
                  </select>

                  <div style={{ position: 'relative', width: '60%' }}>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={
                        searchType === 'Created Date'
                          ? `Cari... (DD-MM-YYYY)`
                          : `Cari ${searchType}...`
                      }
                      style={{
                        width: '100%',
                        fontSize: '0.95rem',
                        border: '1px solid #e9ecef',
                        borderLeft: 'none',
                        borderRadius: '0 8px 8px 0',
                        padding: '0.65rem 1rem',
                        paddingRight: isSearchDebouncing ? '2.5rem' : '1rem',
                        transition: 'all 0.2s ease',
                      }}
                      value={searchQuery}
                      onChange={(e) => onSearchQueryChange(e.target.value)}
                    />
                    {isSearchDebouncing && (
                      <div
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '16px',
                          height: '16px',
                          border: '2px solid #e9ecef',
                          borderTop: '2px solid #059BB6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '0.25rem' }}>
            <ToolbarClassic
              claimType='INPATIENT'
              onModalClose={() => { }}
              onSuccessSubmit={invalidateAll}
            />
          </div>
        </div>
        {loading ? (
          <div
            className='d-flex justify-content-center align-items-center'
            style={{ height: '300px' }}
          >
            <div
              className='spinner-border'
              style={{ color: '#059BB6', width: '3rem', height: '3rem' }}
              role='status'
            >
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className='alert alert-danger m-5' style={{ borderRadius: '8px' }}>
            {error}
          </div>
        ) : filteredClaims.length === 0 ? (
          <div
            className='alert alert-info m-5'
            style={{
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
              backgroundColor: '#EBF7F9',
              border: '1px dashed #059BB6',
              color: '#059BB6',
            }}
          >
            <div className='text-center'>
              <div
                style={{
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  marginBottom: '0.5rem',
                }}
              >
                No claims found
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6B7280' }}>
                {activeTab === 'all'
                  ? 'There are no claims available at the moment.'
                  : 'There are no claims requiring your attention right now.'}
              </div>
            </div>
          </div>
        ) : (
          <div className='card-body' style={{ padding: '0.75rem' }}>
            <div className='table-responsive'>
              <table className='table table-striped gy-5 gs-5' style={{ marginBottom: '1rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Name Member
                    </th>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Claim Number
                    </th>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Card Number
                    </th>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Treatment Type
                    </th>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Customer Group
                    </th>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Created Date
                    </th>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: '1rem',
                        fontWeight: '600',
                        color: '#2D3748',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClaims.map((claim) => (
                    <tr key={claim.id} style={{ transition: 'all 0.2s ease' }}>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>{claim.memberName}</td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>{claim.claimNo}</td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>{claim.cardNumber}</td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        {claim.treatmentType === 'Rawat inap' ? 'Rawat Inap' : 'Rawat Jalan'}
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        {claim.customerName || claim.customerGroup}
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        {moment(claim.claimDate).utcOffset(7).format('DD-MM-YYYY')}
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        <span
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            border: 'none',
                            ...getStatusStyles(claim.status || claim.claimStatus),
                          }}
                        >
                          {/* {claim.status || claim.statusClaim || 'Unknown'} */}
                          {claim.status || claim.claimStatus || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        <div className='d-flex align-items-center gap-2'>
                          <button
                            className='btn btn-icon btn-light-primary btn-sm action-button'
                            onClick={() => handleChatAction(claim)}
                            title='Chat Claim'
                            disabled={loadingClaimNo === claim.claimNo}
                            style={{
                              borderRadius: '6px',
                              width: '32px',
                              height: '32px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              opacity: loadingClaimNo === claim.claimNo ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (loadingClaimNo === claim.claimNo) return
                              const svg = e.currentTarget.querySelector('svg')
                              if (svg) svg.style.color = 'white'
                              e.currentTarget.style.backgroundColor = '#059BB6'
                            }}
                            onMouseLeave={(e) => {
                              if (loadingClaimNo === claim.claimNo) return
                              const svg = e.currentTarget.querySelector('svg')
                              if (svg) svg.style.color = '#059BB6'
                              e.currentTarget.style.backgroundColor = ''
                            }}
                          >
                            {loadingClaimNo === claim.claimNo ? (
                              <div
                                className='spinner-border spinner-border-sm'
                                role='status'
                                style={{ width: '14px', height: '14px' }}
                              >
                                <span className='visually-hidden'>Loading...</span>
                              </div>
                            ) : (
                              <>
                                <FaComment
                                  style={{
                                    color: '#059BB6',
                                    width: '14px',
                                    height: '14px',
                                    transition: 'color 0.2s ease',
                                  }}
                                />
                                {/* Red Dot */}
                                {claim?.chatCounter > 0 && (
                                  <span
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '4px',
                                      minWidth: '8px',
                                      height: '8px',
                                      backgroundColor: 'red',
                                      color: 'white',
                                      borderRadius: '50%',
                                    }}
                                  ></span>
                                )}
                              </>
                            )}
                          </button>
                          <button
                            className='btn btn-icon btn-light-primary btn-sm action-button'
                            onClick={() => handleClaimAction(claim)}
                            title='Detail Claim'
                            disabled={loadingClaimNo === claim.claimNo}
                            style={{
                              borderRadius: '6px',
                              width: '32px',
                              height: '32px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              opacity: loadingClaimNo === claim.claimNo ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (loadingClaimNo === claim.claimNo) return
                              const svg = e.currentTarget.querySelector('svg')
                              if (svg) svg.style.color = 'white'
                              e.currentTarget.style.backgroundColor = '#059BB6'
                            }}
                            onMouseLeave={(e) => {
                              if (loadingClaimNo === claim.claimNo) return
                              const svg = e.currentTarget.querySelector('svg')
                              if (svg) svg.style.color = '#059BB6'
                              e.currentTarget.style.backgroundColor = ''
                            }}
                          >
                            {loadingClaimNo === claim.claimNo ? (
                              <div
                                className='spinner-border spinner-border-sm'
                                role='status'
                                style={{ width: '14px', height: '14px' }}
                              >
                                <span className='visually-hidden'>Loading...</span>
                              </div>
                            ) : (
                              <FaEdit
                                style={{
                                  color: '#059BB6',
                                  width: '14px',
                                  height: '14px',
                                  transition: 'color 0.2s ease',
                                }}
                              />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className='d-flex justify-content-between align-items-center flex-wrap gap-4'>
                <div style={{ color: '#6B7280', fontSize: '0.95rem' }}>
                  {page && page.totalItems
                    ? `Showing ${page.totalItems > 0 ? (page.currentPage - 1) * page.pageSize + 1 : 0
                    } to ${Math.min(page.currentPage * page.pageSize, page.totalItems)} of ${page.totalItems
                    } entries`
                    : `Showing ${filteredClaims.length > 0 ? (currentPage - 1) * pageSize + 1 : 0
                    } to ${Math.min(currentPage * pageSize, filteredClaims.length)} of ${filteredClaims.length
                    } entries`}
                </div>

                <div className='d-flex gap-2'>{renderPagination()}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isChatModalOpen && claimDetails && (
        <ChatModal
          isOpen={isChatModalOpen}
          onClose={() => {
            setIsChatModalOpen(false)
            setClaimDetails(null)
          }}
          message=''
          claimDetails={claimDetails}
          onRefetchDetailsClaim={refetchClaims}
        />
      )}

      <style>
        {`
          .action-button {
            background-color: rgba(5, 155, 182, 0.1);
          }
          
          .action-button:hover {
            background-color: rgba(5, 155, 182, 0.2) !important;
          }
          
          .action-button:hover svg {
            opacity: 0.8;
          }
          
          @keyframes spin {
            0% { transform: translateY(-50%) rotate(0deg); }
            100% { transform: translateY(-50%) rotate(360deg); }
          }
        `}
      </style>
    </>
  )
}

export { BuilderPage }

